<template>
    <div
        class="container container-main py-2 md:py-[16px] bg-white z-10"
        :class="{
            sticky: isOpenMenu || isMobileSearch,
            'header-shadow': isOpenMenu || isMobileSearch,
        }"
    >
        <div v-if="!isMobileSearch" class="flex items-center xl:gap-16">
            <button
                v-if="!isOpenMenu"
                type="button"
                class="w-[32px] h-[32px] md:w-[44px] md:h-[44px] overflow-hidden xl:hidden"
                @click="$emit('toogle-menu')"
            >
                <i-material-symbols-menu-rounded
                    class="text-[27.29px] md:text-[38px] text-primary overflow-hidden"
                />
            </button>

            <button
                v-else
                type="button"
                class="flex items-center p-1 md:p-0 gap-2 uppercase w-[32px] h-[32px] md:w-[44px] md:h-[44px] text-primary bg-background rounded-[5px] overflow-hidden xl:hidden"
                @click="$emit('toogle-menu')"
            >
                <i-material-symbols-close-rounded
                    class="text--md text-[22.3px] md:text-[39px]"
                />
            </button>

            <router-link
                :to="{ name: '/home' }"
                class="mx-auto xl:mr-[24px] xl:mx-0 xl:shrink-0"
                @click="$emit('close-mobile-menu')"
            >
                <figure>
                    <img
                        loading="lazy"
                        src="/img/logo/logo-green.svg"
                        alt="logo"
                        class="w-[93px] md:w-[142px]"
                    />
                </figure>
            </router-link>

            <!-- Desktop input -->
            <div class="hidden items-center gap-2 xl:w-full xl:flex">
                <div class="relative w-full md:py-[3px]">
                    <i-material-symbols-search-rounded
                        class="text-[20px] md:text-[30px] text-primary absolute z-10 right-4 top-[4px] md:top-[7px]"
                    ></i-material-symbols-search-rounded>
                    <input
                        v-model="query"
                        type="text"
                        class="search-input text--sm"
                        :placeholder="$t('common.search')"
                        @focus="$emit('toogle-mobile-search')"
                    />
                </div>

                <button
                    type="button"
                    class="flex items-center p-1 md:p-0 gap-2 uppercase w-[32px] h-[32px] md:w-[44px] md:h-[44px] text-primary bg-background rounded-[5px] xl:hidden"
                    @click="$emit('toogle-mobile-search')"
                >
                    <i-material-symbols-close-rounded
                        class="text--md text-[22.3px] md:text-[39px]"
                    />
                </button>
            </div>

            <div class="flex items-center gap-2">
                <button
                    type="button"
                    class="bg-background rounded-[5px] p-1 w-[32px] h-[32px] md:w-[44px] md:h-[44px] xl:hidden"
                    @click="$emit('toogle-mobile-search')"
                >
                    <i-material-symbols-search-rounded
                        class="text-[20px] md:text-[30px] text-primary"
                    ></i-material-symbols-search-rounded>
                </button>

                <LocalesSwitcher
                    is-for-header
                    class="hidden xl:block bg-background p-2 rounded-[5px]"
                />

                <router-link
                    :to="{ name: '/cart' }"
                    class="relative flex justify-center items-center bg-background rounded-[5px] w-[32px] h-[32px] md:w-[44px] md:h-[44px]"
                >
                    <i-ion-cart class="text-[20px] text-primary"></i-ion-cart>
                    <div
                        v-if="cartStore.getItemsAmount"
                        class="absolute w-5 h-5 -top-[6px] -right-[6px] overflow-hidden flex justify-center items-center text-[16px] font-semibold text-white bg-primary rounded-full"
                    >
                        {{ cartStore.getItemsAmount }}
                    </div>
                </router-link>

                <router-link
                    :to="{ name: '/profile/details/account' }"
                    class="hidden xl:flex justify-center items-center bg-background rounded-[5px] w-[32px] h-[32px] md:w-[44px] md:h-[44px]"
                >
                    <i-mdi-user class="text-[20px] text-primary"></i-mdi-user>
                </router-link>
            </div>
        </div>

        <!-- Search mobile input -->

        <div v-else class="flex items-center gap-2">
            <div class="relative w-full md:py-[3px]">
                <i-material-symbols-search-rounded
                    class="text-[20px] md:text-[30px] text-primary absolute z-10 right-1 top-[4px] md:top-[7px]"
                ></i-material-symbols-search-rounded>
                <input
                    v-model="query"
                    type="text"
                    class="search-input text--sm"
                    :placeholder="$t('common.search')"
                />
            </div>

            <button
                type="button"
                class="flex items-center p-1 md:p-0 gap-2 uppercase w-[32px] h-[32px] md:w-[44px] md:h-[44px] text-primary bg-background rounded-[5px]"
                @click="$emit('toogle-mobile-search')"
            >
                <i-material-symbols-close-rounded
                    class="text--md text-[22.3px] md:text-[39px]"
                />
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, watch } from "vue";
import { useCartStore } from "../../stores/cartStore";
import LocalesSwitcher from "../LocalesSwitcher.vue";

defineProps({
    isOpenMenu: {
        type: Boolean,
        required: true,
    },
    isMobileSearch: {
        type: Boolean,
        required: true,
    },
});

const cartStore = useCartStore();

const emit = defineEmits({
    "toogle-menu": null,
    "toogle-mobile-search": null,
    "close-mobile-menu": null,
    search: (value) => typeof value === "string",
});

const query = ref(null);
const timer = ref(null);

const setQuery = () => {
    clearTimeout(timer.value);

    timer.value = setTimeout(() => {
        emit("search", query.value);
    }, 500);
};

watch(query, () => {
    setQuery();
});
</script>

<style lang="scss" scoped>
.search-input {
    @apply w-full py-[7.5px] md:py-3 px-6  bg-background rounded-[5px];
}

.sticky {
    position: relative;
    @media (max-width: 768px) {
        @apply fixed;
    }
}

.header-shadow {
    @media (max-width: 768px) {
        @apply shadow-[0_4px_4px_rgba(0,0,0,0.05)];
    }
}
</style>
